import type React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MAX_ACTIVE_KNOWLEDGE_PER_TYPE } from "~/constants/knowledge";
import { useProjectPermission } from "~/hooks/useProjectPermission";
import type { Knowledge } from "~/interface/knowledge";
import styles from "./KnowledgeIndicator.module.scss";

/**
 * 【機能概要】: ドキュメントページにナレッジ情報を表示するインジケーター
 * 【実装方針】: 目立つ位置に配置し、クリックでナレッジページへ遷移
 * 【要件対応】: 要件定義書2.4（ナレッジ機能の可視化）に基づく
 * 🟢 信頼性レベル: 低（UI改善だが重要な導線）
 *
 * @param {Knowledge['type']} documentType - ドキュメントタイプ
 * @param {number} activeCount - 有効なナレッジ数
 * @param {number} totalCount - 全ナレッジ数
 * @param {boolean} compact - コンパクト表示モード
 */
interface KnowledgeIndicatorProps {
  documentType: Knowledge["type"];
  activeCount: number;
  totalCount?: number;
  compact?: boolean;
  onOpen?: () => void;
}

const KnowledgeIndicator: React.FC<KnowledgeIndicatorProps> = ({
  documentType,
  activeCount,
  totalCount = 0,
  compact = false,
  onOpen,
}) => {
  const navigate = useNavigate();
  const { organizationId, projectId } = useParams();
  const { canViewKnowledge } = useProjectPermission();

  // 権限チェック: guestは表示不可
  if (!canViewKnowledge()) {
    return null;
  }

  // 【ドキュメントタイプ表示名マッピング】
  const getDocumentTypeLabel = (type: Knowledge["type"]): string => {
    const typeMap: { [key: string]: string } = {
      "system-proposal": "システム概要書",
      "functional-requirements": "機能要件書",
      "non-functional-requirements": "非機能要件書",
      "plugin-proposal": "プラグイン提案書",
      "app-design": "アプリ設計書",
      "field-design": "フィールド設計書",
      "customize-specification": "カスタマイズ仕様書",
    };
    return typeMap[type] || type;
  };

  // 【ナレッジページへ遷移】: クリック時にナレッジページへ移動し、該当タイプで絞り込み
  const handleClick = () => {
    if (onOpen) {
      // サイドバーを開く
      onOpen();
    } else if (organizationId && projectId) {
      // フォールバック: ナレッジページへ遷移
      navigate(`/${organizationId}/project/${projectId}/knowledge?type=${documentType}`);
    }
  };

  // 【表示判定】: 有効なナレッジが0件でも表示して、ユーザーに機能を認知させる
  const hasActiveKnowledge = activeCount > 0;

  if (compact) {
    // 【コンパクトモード】: アイコン + 数字のみ
    return (
      <button
        className={`${styles["indicator-compact"]} ${hasActiveKnowledge ? styles["has-knowledge"] : styles["no-knowledge"]}`}
        onClick={handleClick}
        aria-label={`${getDocumentTypeLabel(documentType)}のナレッジ: ${activeCount}件有効`}
        title={`${getDocumentTypeLabel(documentType)}のナレッジ: ${activeCount}件有効 / ${totalCount}件`}
      >
        <span className={styles["icon"]}>💡</span>
        <span className={styles["count"]}>{activeCount}</span>
      </button>
    );
  }

  // 【通常モード】: フル表示
  return (
    <button
      className={`${styles["indicator"]} ${hasActiveKnowledge ? styles["has-knowledge"] : styles["no-knowledge"]}`}
      onClick={handleClick}
      aria-label={`${getDocumentTypeLabel(documentType)}のナレッジを管理`}
    >
      <div className={styles["content"]}>
        <div className={styles["header"]}>
          <span className={styles["icon"]}>💡</span>
          <span className={styles["label"]}>ナレッジ</span>
        </div>
        <div className={styles["stats"]}>
          <div className={styles["statsRow"]}>
            <span className={styles["statsLabel"]}>有効:</span>
            <span className={styles["active-count"]}>
              {activeCount}/{MAX_ACTIVE_KNOWLEDGE_PER_TYPE}件
            </span>
          </div>
          {totalCount > 0 && (
            <div className={styles["statsRow"]}>
              <span className={styles["statsLabel"]}>総数:</span>
              <span className={styles["total-count"]}>{totalCount}件</span>
            </div>
          )}
        </div>
        {!hasActiveKnowledge && (
          <div className={styles["cta"]}>
            <span className={styles["cta-text"]}>ナレッジを追加 →</span>
          </div>
        )}
        {hasActiveKnowledge && (
          <div className={styles["cta"]}>
            <span className={styles["cta-text"]}>管理する →</span>
          </div>
        )}
      </div>
    </button>
  );
};

export default KnowledgeIndicator;
